import { useEffect, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { FiEdit2, FiArchive, FiChevronDown } from "react-icons/fi";

import CustomerService from "src/Services/API/customer.service";
import BranchService from "src/Services/API/branch.service";
import { setCustomerName } from "src/Redux/Slices/HeaderSlice";
import { getImageUrl } from "src/Common/common";
import { can } from "src/Common/function";

import LocationAddModal from "./components/LocationAddModal";
import RestoreDialog from "./components/RestoreDialog";
import { StatusBadge } from "./components/components";
import CustomerWoMockup from "./WO/CustomerWoMockup";
import CustomerFormsMockup from "./WO/CustomerFormsMockup";
import CustomerTemplateList from "./WO/CustomerTemplateList";

const LocationDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { locationId } = useParams();

  const [customer, setCustomer] = useState(null);
  const [account, setAccount] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const [sections, setSections] = useState({ wo: true, forms: false, templates: false });
  const [woCount, setWoCount] = useState(0);
  const [formCount, setFormCount] = useState(0);
  const [templateCount, setTemplateCount] = useState(0);

  // A location is the Customer entity; its write ops use customer:*Location.
  const canEditLocation = can("customer:editLocation");
  const canArchiveLocation = can("customer:archiveLocation");

  useEffect(() => {
    BranchService.getAll().then((response) => setBranches(response.data || []));
  }, []);

  useEffect(() => {
    if (locationId) fetchCustomer();
  }, [locationId]);

  const fetchCustomer = async () => {
    setIsLoading(true);
    try {
      const customerRes = await CustomerService.getOne(locationId);
      const customerData = customerRes.data;
      setCustomer(customerData);

      const accountObj = customerData?.account || null;
      setAccount(accountObj);
      const title = accountObj?.name ? `${accountObj.name}/${customerData.companyName}` : customerData.companyName;
      dispatch(setCustomerName(title));
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load location");
    } finally {
      setIsLoading(false);
    }
  };

  const updateLocation = async (location) => {
    try {
      await CustomerService.updateCustomer(location);
      toast("Location updated successfully");
      setIsEditOpen(false);
      await fetchCustomer();
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to update location");
    }
  };

  const confirmArchive = async () => {
    try {
      await CustomerService.archiveCustomer(Number(locationId));
      toast("Location archived");
      if (account?.id) navigate(`/customer/${account.id}`);
      else navigate("/customer");
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to archive location");
    }
  };

  const toggleSection = (key) => setSections((prev) => ({ ...prev, [key]: !prev[key] }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Oval height={50} width={50} strokeWidth={2} color="#010D3D" secondaryColor="#80869E" visible />
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white px-6 py-5">
      {isEditOpen && (
        <LocationAddModal
          branches={branches}
          customers={[customer]}
          customer={customer}
          onUpateClicked={updateLocation}
          onCancelClicked={() => setIsEditOpen(false)}
        />
      )}
      {archiveOpen && (
        <RestoreDialog
          variant="archive"
          title="Are you sure you want to archive this location?"
          description={[
            "Archived locations will no longer appear when creating new records.",
            "Historical records will remain unchanged.",
          ]}
          confirmLabel="Archive Location"
          onConfirm={confirmArchive}
          onCancel={() => setArchiveOpen(false)}
        />
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center cursor-pointer" onClick={() => navigate(-1)}>
            <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" alt="back" />
            <span className="font-OpenSansBold text-[14px] text-subTitleColor">back</span>
          </div>
        </div>
        {(canEditLocation || canArchiveLocation) && (
          <div className="flex items-center gap-4">
            {canEditLocation && (
            <button
              onClick={() => setIsEditOpen(true)}
              className="h-[32px] px-3 rounded-[8px] bg-blueColor flex items-center justify-center gap-2 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 13 13" fill="none">
                <path
                  d="M12.4994 11.3332C12.7755 11.3332 12.9994 11.557 12.9994 11.8332C12.9994 12.1093 12.7755 12.3332 12.4994 12.3332H6.49938C6.22323 12.3332 5.99938 12.1093 5.99938 11.8332C5.99938 11.557 6.22323 11.3332 6.49938 11.3332H12.4994Z"
                  fill="white"
                />
                <path
                  d="M11.3327 1.91536C11.3327 1.6727 11.2361 1.43985 11.0645 1.26823C10.8929 1.0966 10.6601 1 10.4174 1C10.1747 1 9.94185 1.09661 9.77022 1.26823L1.76306 9.27539C1.664 9.3744 1.59138 9.49707 1.55213 9.63151L1.07817 11.2539L2.70121 10.7806C2.83572 10.7414 2.95891 10.6687 3.05798 10.5697L11.0645 2.5625C11.2361 2.39092 11.3327 2.158 11.3327 1.91536ZM12.3327 1.91536C12.3327 2.42322 12.1306 2.91042 11.7715 3.26953L3.76502 11.2767C3.54696 11.4947 3.27724 11.654 2.98116 11.7402L1.06645 12.2988C0.923129 12.3406 0.770948 12.3437 0.626344 12.3066C0.481734 12.2696 0.349769 12.1941 0.244183 12.0885C0.138548 11.9829 0.0631595 11.8504 0.0260841 11.7057C-0.010928 11.5611 -0.00856058 11.409 0.0332455 11.2656L0.591839 9.35091C0.678205 9.05516 0.838116 8.78617 1.05603 8.56836L9.06319 0.561198C9.42235 0.20204 9.90943 4.37772e-06 10.4174 0C10.9253 0 11.4124 0.202037 11.7715 0.561198C12.1306 0.92035 12.3327 1.40748 12.3327 1.91536Z"
                  fill="white"
                />
                <path
                  d="M8.14586 1.47964C8.34112 1.28438 8.65763 1.28438 8.85289 1.47964L10.8529 3.47964C11.0482 3.6749 11.0482 3.99141 10.8529 4.18667C10.6576 4.38193 10.3411 4.38193 10.1459 4.18667L8.14586 2.18667C7.9506 1.99141 7.9506 1.6749 8.14586 1.47964Z"
                  fill="white"
                />
              </svg>{" "}
              Edit
            </button>
            )}
            {canArchiveLocation && (
            <button
              onClick={() => setArchiveOpen(true)}
              className="h-[32px] px-3 rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 -outline-offset-1 outline-[#C9CEE2] flex items-center justify-center gap-2 text-[14px] font-OpenSansSemiBold text-titleColor capitalize leading-5 tracking-[0.14px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13" viewBox="0 0 15 13" fill="none">
                <path
                  d="M13.3333 1.16667C13.3333 1.07462 13.2587 1 13.1667 1H1.16667C1.07462 1 1 1.07462 1 1.16667V3.16667C1 3.25871 1.07462 3.33333 1.16667 3.33333H13.1667C13.2587 3.33333 13.3333 3.25871 13.3333 3.16667V1.16667ZM14.3333 3.16667C14.3333 3.811 13.811 4.33333 13.1667 4.33333H1.16667C0.522334 4.33333 0 3.811 0 3.16667V1.16667C0 0.522334 0.522334 0 1.16667 0H13.1667C13.811 0 14.3333 0.522334 14.3333 1.16667V3.16667Z"
                  fill="#1A2332"
                />
                <path
                  d="M1.33333 11.1667V3.83333C1.33333 3.55719 1.55719 3.33333 1.83333 3.33333C2.10948 3.33333 2.33333 3.55719 2.33333 3.83333V11.1667C2.33333 11.3877 2.42119 11.5996 2.57747 11.7559C2.73375 11.9121 2.94565 12 3.16667 12H11.1667C11.3877 12 11.5996 11.9121 11.7559 11.7559C11.9121 11.5996 12 11.3877 12 11.1667V3.83333C12 3.55719 12.2239 3.33333 12.5 3.33333C12.7761 3.33333 13 3.55719 13 3.83333V11.1667C13 11.6529 12.8067 12.1191 12.4629 12.4629C12.1191 12.8067 11.6529 13 11.1667 13H3.16667C2.68044 13 2.21426 12.8067 1.87044 12.4629C1.52663 12.1191 1.33333 11.6529 1.33333 11.1667Z"
                  fill="#1A2332"
                />
                <path
                  d="M8.5 6C8.77614 6 9 6.22386 9 6.5C9 6.77614 8.77614 7 8.5 7H5.83333C5.55719 7 5.33333 6.77614 5.33333 6.5C5.33333 6.22386 5.55719 6 5.83333 6H8.5Z"
                  fill="#1A2332"
                />
              </svg>{" "}
              Archive
            </button>
            )}
          </div>
        )}
      </div>

      {/* Location Information */}
      <div className="bg-white border border-[#E5E8F3] rounded-[12px] overflow-hidden mb-6 shadow-[0px_1px_2px_rgba(0,0,0,0.05)]">
        <div className="px-6 py-4 bg-[#F8F9FE] border-b border-[#E5E8F3] flex items-center justify-between">
          <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] capitalize tracking-[0.14px]">
            Location Information
          </span>
          <StatusBadge archived={customer?.isArchived} />
        </div>
        <div className="px-6 py-6 flex flex-wrap content-start items-start gap-8">
          <InfoField label="Customer" value={account?.name || "—"} />
          <InfoField label="Location Name" value={customer?.companyName || "—"} />
          <InfoField label="Branch" value={customer?.branch?.name || "—"} />
          <InfoField label="Contact name" value={customer?.contact || "—"} />
          <InfoField label="Email" value={customer?.email || "—"} capitalize={false} />
          <InfoField label="Phone Number" value={customer?.phone || "—"} capitalize={false} />
          <InfoField label="Address" value={customer?.address || "—"} />
        </div>
      </div>

      {/* Work Orders */}
      <Section title={vocabularize("Work Orders")} count={woCount} open={sections.wo} onToggle={() => toggleSection("wo")}>
        <CustomerWoMockup locationId={locationId} onCountChange={setWoCount} />
      </Section>

      {/* Forms */}
      <Section title="Forms" count={formCount} open={sections.forms} onToggle={() => toggleSection("forms")}>
        <CustomerFormsMockup locationId={locationId} onCountChange={setFormCount} />
      </Section>

      {/* Templates */}
      <Section
        title="Templates"
        count={templateCount}
        open={sections.templates}
        onToggle={() => toggleSection("templates")}
      >
        <CustomerTemplateList locationId={locationId} onCountChange={setTemplateCount} />
      </Section>
    </div>
  );
};

function InfoField({ label, value, capitalize = true }) {
  return (
    <div className="flex-1 min-w-[240px] max-w-[246px] flex flex-col gap-1">
      <p className="text-[12px] font-OpenSansSemiBold text-[#7C829A] uppercase tracking-[0.12px] leading-[18px]">
        {label}
      </p>
      <p
        className={`text-[14px] font-OpenSansSemiBold text-[#1A2332] tracking-[0.14px] leading-5 break-words ${
          capitalize ? "capitalize" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Section({ title, count, open, onToggle, action, children }) {
  return (
    <div className="bg-white border border-[#E5E8F3] rounded-[12px] overflow-hidden mb-6 shadow-[0px_1px_2px_rgba(0,0,0,0.05)]">
      <div
        onClick={onToggle}
        className="px-6 py-4 bg-[#F8F9FE] border-b border-[#E5E8F3] flex items-center justify-between cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] capitalize tracking-[0.14px]">{title}</span>
          {typeof count === "number" && (
            <span className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 rounded-full bg-[#EEF0F9] text-[12px] font-OpenSansSemiBold text-[#5F6477] tracking-[0.12px]">
              {count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {action}
          <FiChevronDown
            size={18}
            className={`text-[#7C829A] transition-transform duration-300 ease-in-out ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>
      {/* Keep children mounted (just collapsed) so they can fetch and report
          their count to the header badge even while the section is collapsed.
          The grid-rows trick animates from 0fr -> 1fr for a smooth height transition. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

export default LocationDetail;
